import { assertLinkRequestActive } from '#~/api/agent/link-request-expiry.js';
import type { LinkRequestLifetime } from '#~/api/agent/link-request-expiry.js';
import { LinkRequestLifetimeNotice, useLinkRequestLifetime } from './link-request-lifetime.jsx';
import { useRenderedBootHumanWait } from '#~/routes/boot-overlay/boot-budget-view.js';
import * as Wizard from '#~/agent/components/wizard/index.js';
import { createRoom, fetchRequestLink, listOrganizations, listRooms } from '#~/api/agent/index.js';
import type { CreateRoomResult, LinkRequest } from '#~/api/agent/index.js';
import { getOrCreateCreateRoomIdempotencyKey } from '#~/api/agent/idempotency-key.js';
import { toHexFromId, toIdFromHex } from '#~/api/index.js';
import type { ApiError } from '#~/api/index.js';
import { useAuth0 } from '#~/auth/index.js';
import { NotFoundError } from '#~/error/index.js';
import { Paths } from '#~/routes/index.js';
import { DataSTag, Sel, STag } from '#~/stag/tag.js';
import { Problem } from './problem.jsx';
import { Anchor } from '@studio/design-system/button';
import { Icon } from '@studio/design-system/icon';
import { Input } from '@studio/design-system/input';
import { Loader } from '@studio/design-system/loader';
import * as Select from '@studio/design-system/select';
import T from '@studio/design-system/themes';
import { unwrap } from '@studio/js-utils/async';
import { normalizeId } from '@studio/models';
import type { Agent } from '@studio/models/agent';
import type { Room } from '@studio/models/room';
import { useAsyncEffect, useLatestFn } from '@studio/react-utils/hooks';
import type {
  AgentRegistryGetOrganizationResponse,
  AgentRegistryGetRoomResponse
} from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { noop } from 'lodash-es';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

/** `<Confirm>` component. */
export const CreateStudio = (): ReactElement => {
  const params = useParams();
  const location = useLocation();

  const shouldAutoCreate = location.state?.shouldAutoCreate ?? false;

  const agentId = normalizeId<Agent['id']>(params.agentId);
  const requestId = normalizeId<LinkRequest['id']>(params.requestId);

  if (!agentId || !requestId) {
    throw new NotFoundError();
  }

  const [agent, setAgent] = useState<
    Omit<Agent, 'lastShutDownAt' | 'roomId' | 'status'> & LinkRequestLifetime
  >();
  const [roomName, setRoomName] = useState<string>('My Studio');
  const [rooms, setRooms] = useState<AgentRegistryGetRoomResponse[]>();

  const [selectedOrgId, setSelectedOrgId] = useState<Uint8Array>();
  const [orgs, setOrgs] = useState<AgentRegistryGetOrganizationResponse[]>();

  const [isBusy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError>();
  const lifetime = useLinkRequestLifetime(agent?.linkExpiresAt, error);
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  useAsyncEffect(
    function* generator(signal: AbortSignal) {
      try {
        const token = yield* unwrap(getAccessTokenSilently());

        const response = yield* unwrap(fetchRequestLink(requestId, token));

        setAgent({ ...response, id: agentId });

        const listedOrgs = (yield* unwrap(listOrganizations(token))).filter(
          (org) => org.permissions?.canCreateRoom
        );

        setOrgs(listedOrgs);

        const [firstOrg] = listedOrgs;

        if (firstOrg) {
          setSelectedOrgId(firstOrg.id);
        }
      } catch (error_) {
        if (!signal.aborted) {
          setError(error_ as ApiError);
        }
      }
    },
    [requestId, setOrgs, setSelectedOrgId]
  );

  useAsyncEffect(
    function* generator(signal: AbortSignal) {
      try {
        const token = yield* unwrap(getAccessTokenSilently());

        const response = yield* unwrap(fetchRequestLink(requestId, token));

        setAgent({ ...response, id: agentId });

        setRooms(yield* unwrap(listRooms(token)));
      } catch (error_) {
        if (!signal.aborted) {
          setError(error_ as ApiError);
        }
      }
    },
    [requestId, setRooms]
  );

  const handleSelectStudio = useLatestFn((event: React.MouseEvent) => {
    event.preventDefault();

    navigate(Paths.agent.link.selectStudio.resolve(agentId, requestId));
  });

  const handleLinkAgent = useLatestFn(async (roomId: Room['id'], alreadyExisted?: boolean) => {
    setBusy(true);

    try {
      // Instead of directly linking the agent, we'll redirect the user to new the auth route:
      // `alreadyExisted` rides along in router state (ENG-4209) so the auth step —
      // the screen the user actually stops on — can report "linked to your existing
      // Studio" vs "new Studio created". Reporting it HERE cannot work: this
      // component navigates away in the same turn, and on the auto-create path
      // `isBusy` stays true, so its own <Loader /> early-return replaces the page.
      navigate(Paths.agent.link.auth.resolve(agentId, requestId, roomId), {
        state: { alreadyExisted }
      });
    } catch (error_) {
      setBusy(false);
      setError(error_ as ApiError);
    }
  });

  const handleCreateStudio = useLatestFn(async (): Promise<T_Maybe<CreateRoomResult>> => {
    setBusy(true);

    try {
      // Stable per-attempt idempotency key (ENG-4209): keyed by the link
      // requestId and persisted in sessionStorage, so a network retry OR a
      // remount/reload of the SAME link attempt reuses it and the server dedups
      // instead of creating a second Studio. A different link request is a
      // genuinely new attempt and gets its own key.
      // Per rule: reference-rules-in-comments (ai.knowledge/global).
      const idempotencyKey = getOrCreateCreateRoomIdempotencyKey(requestId);

      const token = await getAccessTokenSilently();
      assertLinkRequestActive(agent?.linkExpiresAt);
      return await createRoom(roomName!, token, selectedOrgId, idempotencyKey);
    } catch (error_) {
      setBusy(false);
      setError(error_ as ApiError);

      return undefined;
    }
  });

  const handleCreateStudioAndLinkAgent = useLatestFn(async () => {
    const created = await handleCreateStudio();

    if (created) {
      await handleLinkAgent(created.id, created.alreadyExisted);
    }
  });

  // Track if we've attempted auto-creation
  const [hasAttemptedAutoCreate, setHasAttemptedAutoCreate] = useState(false);

  // Auto-create studio and link agent when shouldAutoCreate is true
  useEffect(() => {
    // Only require agent to be loaded - createRoom can handle undefined selectedOrgId
    if (shouldAutoCreate && !hasAttemptedAutoCreate && agent && !lifetime.expired) {
      setHasAttemptedAutoCreate(true);
      handleCreateStudioAndLinkAgent().catch(noop);
    }
  }, [
    shouldAutoCreate,
    handleCreateStudioAndLinkAgent,
    agent,
    hasAttemptedAutoCreate,
    lifetime.expired
  ]);

  useRenderedBootHumanWait(!isBusy && !shouldAutoCreate && (!!agent || !!error || !!rooms));

  // Show loader while data is being fetched
  // Let an already submitted claim settle; expiry prevents new actions.
  if (lifetime.expired && !isBusy) return <LinkRequestLifetimeNotice {...lifetime} />;

  if (!agent && !error && !rooms) {
    return <Loader />;
  }

  if (shouldAutoCreate && (!agent || isBusy || !hasAttemptedAutoCreate) && !error) {
    return <Loader />;
  }

  const isDataValid = (roomName?.length ?? 0) > 3;

  return (
    <Wizard.Root currentStep={1} totalSteps={3}>
      <LinkRequestLifetimeNotice {...lifetime} />
      {!!agent && (
        <div className='w-full overflow-hidden'>
          <Icon className='mb-16 text-[128px] text-primary' icon='home' />
          <div className='mb-4 text-lg font-demibold'>
            {roomName && roomName.length !== 0 ? roomName : 'My Studio'}
          </div>
          <Wizard.Separator />
        </div>
      )}

      {!!error && (
        <Problem agent={{ ...agent, id: agentId }} error={{ ...error, title: 'Linking Error' }}>
          <Anchor
            className='text-primary'
            onClick={(): void => {
              navigate(Paths.agent.link.confirm.resolve(agentId, requestId));
            }}
          >
            Try again
          </Anchor>
        </Problem>
      )}

      {!!agent && !error && (
        <>
          <Wizard.Verbiage
            message='The Studio is your central hub for camera management and team collaboration, usually named after where your cameras are physically located.'
            title='Create A Studio'
          />

          {(orgs?.length ?? 0) > 1 && (
            <Select.Root
              onValueChange={(v: string): void => {
                setSelectedOrgId(toIdFromHex(v));
              }}
              size='xl'
              value={selectedOrgId ? toHexFromId(selectedOrgId) : undefined}
              variant='light'
            >
              <Select.Trigger
                className={selectedOrgId === undefined ? 'text-muted' : undefined}
                key={selectedOrgId === undefined ? 'trigger-placeholder' : 'trigger-active'}
                // Test-observability only (ENG-4209 retry staging): names this control
                // for e2e. Same pattern as device/components/config/brand.tsx.
                {...STag(Sel.AgentLinkCreateStudio, 'org')}
              >
                <Select.Value placeholder='Select Organization' />
              </Select.Trigger>

              <Select.Content>
                {orgs?.map((org) => (
                  <Select.Item
                    key={toHexFromId(org.id)}
                    value={toHexFromId(org.id)!}
                    // Identity-suffixed so a test can pick a SPECIFIC organization
                    // rather than an index (brand.tsx item pattern).
                    {...STag(Sel.AgentLinkCreateStudio, 'org', toHexFromId(org.id))}
                  >
                    {org.name}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          )}

          {!!selectedOrgId && (
            <Input
              data-theme={T.getTheme('light')}
              focusOnMount
              onChange={setRoomName}
              placeholder='My Studio'
              selectOnMount
              size='xl'
              value={roomName}
              // Read-only exposure of the RESOLVED organization. This input is
              // rendered only once `selectedOrgId` is set, and the wizard renders no
              // `<Select>` when there is a single candidate — so this attribute is the
              // only seam that names the organization a create will use in that state.
              // Inert: no behavior, ordering or default selection depends on it.
              {...DataSTag(Sel.AgentLinkCreateStudio, 'selected-org', toHexFromId(selectedOrgId))}
            />
          )}

          <Wizard.ButtonBox>
            <Wizard.Button
              busy={isBusy}
              disabled={isBusy || !isDataValid}
              onClick={(): void => {
                handleCreateStudioAndLinkAgent().catch(noop);
              }}
            >
              {isBusy
                ? `Creating Studio & Linking ${agent.embedded ? 'Camera' : 'Agent'}...`
                : `Create Studio & Link ${agent.embedded ? 'Camera' : 'Agent'}`}
            </Wizard.Button>

            {(rooms?.length ?? 0) > 0 && (
              <Anchor className='text-primary' onClick={handleSelectStudio}>
                Choose an existing Studio
              </Anchor>
            )}
          </Wizard.ButtonBox>
        </>
      )}
    </Wizard.Root>
  );
};
